/**
 * Function naming convention: api{FunctionName}
 * File naming convention: {FunctionName}.ts
 */

import { API_REQUEST_PARAMS } from "@gameApp/api/constants";
import { apiAbortControllers } from "@gameApp/api/helpers/abortControllers";
import type { ApiType } from "@gameApp/api/types";
import { isFunction } from "@gameApp/utils/isFunction";
import { version } from "@root/package.json";
import isObject from "lodash/isObject";

export interface APIResponseBody<Body> {
  ok: boolean;
  err: string;
  statusCode: number;
  statusText: string;
  body: Body;
}

/**
 * @TODO Refactoring: Pass params as an object instead of multiple arguments
 */
export async function apiRequest<T>(
  path: string,
  options?: RequestInit,
  apiType: ApiType = "EBI",
  accessToken?: string | null,
  beforeSend?: ((options: RequestInit) => void) | null,
  timeout = 60000,
): Promise<APIResponseBody<T>> {
  try {
    const { baseUrl, options: defaultOptions } =
      API_REQUEST_PARAMS[apiType] || {};

    const headers = new Headers(defaultOptions.headers);

    if (version) {
      headers.set("App-Version", version);
    }

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const requestUrl = addPathToUrl(path, baseUrl);
    const { signal } = apiAbortControllers.create(requestUrl);

    const id = setTimeout(
      () => apiAbortControllers.cancel(requestUrl),
      timeout,
    );

    const requestOptions = {
      ...defaultOptions,
      ...(isObject(options) ? options : {}),
      signal,
      headers,
    };

    if (isFunction(beforeSend)) {
      beforeSend(requestOptions);
    }

    const res = await fetch(requestUrl, {
      ...defaultOptions,
      ...(isObject(options) ? { ...options, signal } : {}),
      headers,
    });

    clearTimeout(id);

    const method = options?.method || defaultOptions.method;
    let body = {} as T;

    try {
      body = await res.json();
    } catch (err) {
      body = {
        message: res.statusText,
      } as T;
    }

    if ((res.status === 200 && method === "DELETE") || res.status === 204) {
      return {
        ok: true,
        err: "",
        statusCode: res.status,
        statusText: res.statusText,
        body: true as T,
      };
    }

    if (!res.ok) {
      return {
        ok: false,
        err: (body as { message: string })?.message || "",
        statusCode: res.status,
        statusText: res.statusText,
        body: null as T,
      };
    }

    return {
      ok: res.ok,
      err: "",
      statusCode: res.status,
      statusText: res.statusText,
      body,
    };
  } catch (err) {
    console.warn(err);
    let errorMessage = "";

    if (err instanceof Error) {
      if (err.name === "TimeoutError") {
        errorMessage = "Request Timeout";
      } else if (err.name === "AbortError") {
        errorMessage = "Request Aborted";
      } else {
        errorMessage = err.message;
      }
    }

    return {
      ok: false,
      err: errorMessage,
      statusCode: 520,
      statusText: "Unexpected error occurred",
      body: null as T,
    };
  }
}

function addPathToUrl(path: string, baseUrl: string): string {
  return `${baseUrl.replace(/\/$/g, "")}/${path.replace(/^\//g, "")}`;
}
